import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { color } from '@nuvolo/nuux-theme/utils';
import type { CustomChipColors } from '#/common/utils/wrappers/withChip';
import FAIcon from '#/common/components/FAIcon/FAIcon';
import { borderRadius, fontSizes } from '#/common/hooks/useClientTheme';
import { useSwitch } from '@nuvolo/nuux-core/hooks/useSwitch';

export type ActionChipItem = {
  key: string;
  label: string;
};

export interface ActionChipProps {
  value: string;
  items: ActionChipItem[];
  onChange: (value: string) => void;
  customColors?: CustomChipColors;
  menuWidth?: number;
  emptyLabel?: string;
}

const TriggerButton = styled.button<{ $colors?: CustomChipColors }>`
  width: auto;
  max-width: 100%;
  min-height: 24px;
  border-radius: ${borderRadius?.base || '8px'};
  border: none;
  padding: 8px 8px 8px 8px;
  background-color: ${(props) => props.$colors?.backgroundColor || color('ui', 'light3')};
  color: ${(props) => props.$colors?.textColor || color('ui', 'dark')};
  font-weight: 400;
  font-size: ${fontSizes?.base || '14px'};
  line-height: 1;
  cursor: pointer;
  box-shadow: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  white-space: nowrap;
  overflow: hidden;
`;

const LabelSpan = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1;
`;

const Menu = styled.div`
  position: fixed;
  z-index: 1000;
  background: ${color('custom', 'fancyDropDownBackground')};
  border: 1px solid ${color('ui', 'dark', 0.2)};
  border-radius: ${borderRadius?.base || '8px'};
  box-shadow: 0 10px 20px ${color('ui', 'dark', 0.25)};
  overflow: hidden;
`;

const MenuItem = styled.button<{ $active: boolean }>`
  width: 100%;
  border: none;
  background: ${(props) =>
    props.$active ? color('custom', 'focusItemBackground') : 'transparent'};
  color: ${color('custom', 'contrastText')};
  padding: 6px 10px;
  font-size: ${fontSizes?.small || '12px'};
  font-weight: 400;
  line-height: 1.1;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;

  &:hover {
    background: ${color('custom', 'focusItemBackground')};
  }
`;

const MenuItemIconSlot = styled.span`
  width: 12px;
  min-width: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const MenuItemLabel = styled.span`
  flex: 1;
  font-size: ${fontSizes?.base || '14px'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const ActionChip: React.FC<ActionChipProps> = ({
  value,
  items,
  onChange,
  customColors,
  menuWidth = 120,
  emptyLabel,
}) => {
  const { value: isVisible, on: show, off: hide } = useSwitch(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: menuWidth });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const normalizedValue = useMemo(() => {
    if (value === '$null' || value === 'null') {
      return '';
    }

    return value;
  }, [value]);

  const selectedLabel = useMemo(() => {
    if (!normalizedValue.length) {
      return emptyLabel ?? normalizedValue;
    }

    return items.find((item) => item.key === normalizedValue)?.label ?? normalizedValue;
  }, [emptyLabel, items, normalizedValue]);

  const openMenu = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: menuWidth,
    });
    show();
  }, [menuWidth]);

  useEffect(() => {
    if (!isVisible) return;

    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      hide();
    };

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
    };

    const onWindowUpdate = () => hide();

    document.addEventListener('mousedown', onMouseDown);
    globalThis.addEventListener('keydown', onEscape);
    globalThis.addEventListener('scroll', onWindowUpdate, true);
    globalThis.addEventListener('resize', onWindowUpdate);

    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      globalThis.removeEventListener('keydown', onEscape);
      globalThis.removeEventListener('scroll', onWindowUpdate, true);
      globalThis.removeEventListener('resize', onWindowUpdate);
    };
  }, [isVisible, hide]);

  return (
    <>
      <TriggerButton
        ref={triggerRef}
        type="button"
        $colors={customColors}
        onClick={() => (isVisible ? hide() : openMenu())}
        aria-label="Action"
        aria-haspopup="listbox"
        aria-expanded={isVisible}>
        <LabelSpan>{selectedLabel}</LabelSpan>
        <span>
          <FAIcon icon="angle-down" size="xs" color={customColors?.textColor} />
        </span>
      </TriggerButton>

      {isVisible &&
        createPortal(
          <Menu
            ref={menuRef}
            role="listbox"
            style={{ top: menuPosition.top, left: menuPosition.left, width: menuPosition.width }}>
            {items.map((item) => (
              <MenuItem
                key={item.key}
                type="button"
                role="option"
                aria-selected={item.key === normalizedValue}
                $active={item.key === normalizedValue}
                onClick={() => {
                  onChange(item.key);
                  hide();
                }}>
                <MenuItemIconSlot>
                  {item.key === normalizedValue ? <FAIcon icon="check" size="xs" /> : null}
                </MenuItemIconSlot>
                <MenuItemLabel>{item.label}</MenuItemLabel>
              </MenuItem>
            ))}
          </Menu>,
          document.body,
        )}
    </>
  );
};

export default React.memo(ActionChip);
